import { indexedDBManager, SyncItem } from './indexedDB';
import { referenceDataSyncService } from '../services/referenceDataSyncService';
import { API_ENDPOINTS, BASE_URL } from '../constants/apiEndpoints';
import { getLanguage } from '../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../constants/environmentConfig';

export interface SyncConfig {
  baseUrl: string;
  batchSize: number;
  retryAttempts: number;
  retryDelay: number;
  syncInterval: number;
}

export interface SyncResult {
  success: boolean;
  syncedItems: number;
  failedItems: number;
  errors: string[];
}

export class SyncEngine {
  private config: SyncConfig;
  private isRunning = false;
  private syncInterval: number | null = null;

  constructor(config: Partial<SyncConfig> = {}) {
    this.config = {
      baseUrl: config.baseUrl || BASE_URL.CONSUMER_PRICE_BASE,
      batchSize: config.batchSize || 10,
      retryAttempts: config.retryAttempts || 3,
      retryDelay: config.retryDelay || 5000,
      syncInterval: config.syncInterval || ENVIRONMENT_CONFIG.SYNC_ENGINE_INTERVAL,
    };
  }

  async init(): Promise<void> {
    try {
      await referenceDataSyncService.init();
    } catch (error) {
      throw error;
    }
  }

  async startAutoSync(): Promise<void> {
    if (this.isRunning) return;

    this.isRunning = true;

    referenceDataSyncService.startAutoSync().catch((error) => {
      console.warn('Failed to start reference data auto sync:', error);
    });

    if (navigator.onLine) {
      this.sync().catch((error) => {
        console.warn('Initial sync queue execution failed or timed out:', error);
      });
    }

    this.syncInterval = setInterval(async () => {
      if (navigator.onLine) {
        await this.sync();
      }
    }, this.config.syncInterval);
  }

  async stop(): Promise<void> {
    this.isRunning = false;
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    await referenceDataSyncService.stopAutoSync();
  }

  async sync(): Promise<SyncResult> {
    if (!navigator.onLine) {
      return {
        success: false,
        syncedItems: 0,
        failedItems: 0,
        errors: ['No internet connection'],
      };
    }

    const allUnsyncedItems = await indexedDBManager.getUnsyncedItems();

    const unsyncedItems = allUnsyncedItems.filter((item) => {
      if ((item as unknown as { action: string }).action === 'delete') {
        return false;
      }

      if (item.entity === 'mainProduct' || item.entity === 'basket') {
        return false;
      }

      if (
        item.entity === 'presentation' ||
        item.entity === 'price' ||
        item.entity === 'inspection'
      ) {
        if (item.action === 'create' && !item.data.refId) {
          return false;
        }
        if (
          item.action === 'update' &&
          item.data.id &&
          !String(item.data.id).includes('_') &&
          !item.data.refId
        ) {
          return false;
        }
      }
      return true;
    });

    if (unsyncedItems.length === 0) {
      return {
        success: true,
        syncedItems: 0,
        failedItems: 0,
        errors: [],
      };
    }

    const batches = this.createBatches(unsyncedItems);
    const result: SyncResult = {
      success: true,
      syncedItems: 0,
      failedItems: 0,
      errors: [],
    };

    for (const batch of batches) {
      const batchResult = await this.syncBatch(batch);
      result.syncedItems += batchResult.syncedItems;
      result.failedItems += batchResult.failedItems;
      result.errors.push(...batchResult.errors);
    }
    return result;
  }

  async forceSyncAll(): Promise<SyncResult> {
    return await this.sync();
  }

  private createBatches(items: SyncItem[]): SyncItem[][] {
    const batches: SyncItem[][] = [];
    for (let i = 0; i < items.length; i += this.config.batchSize) {
      batches.push(items.slice(i, i + this.config.batchSize));
    }
    return batches;
  }

  private async syncBatch(batch: SyncItem[]): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      syncedItems: 0,
      failedItems: 0,
      errors: [],
    };

    for (const item of batch) {
      try {
        const success = await this.syncItem(item);
        if (success) {
          await indexedDBManager.markAsSynced(item.id);
          result.syncedItems++;
        } else {
          await indexedDBManager.incrementRetryCount(item.id);
          result.failedItems++;
        }
      } catch (error) {
        await indexedDBManager.incrementRetryCount(item.id);
        result.failedItems++;
        result.errors.push(`Failed to sync ${item.entity} ${item.action}: ${error}`);
      }
    }

    return result;
  }

  private async syncItem(item: SyncItem): Promise<boolean> {
    const endpoint = this.getEndpointForEntity(item.entity);
    const authToken = await this.getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': getLanguage(),
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    try {
      let response: Response;
      let requestData: any = { ...item.data };

      if (item.action === 'create' && requestData.id) {
        requestData.refId = requestData.id;
        delete requestData.id;
      }

      switch (item.action) {
        case 'create':
        case 'update':
        case 'soft-delete': {
          response = await fetch(`${this.config.baseUrl}${endpoint}/upsert`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(requestData),
          });
          break;
        }

        default:
          throw new Error(`Unknown action: ${item.action}`);
      }

      if (!response.ok) {
        return false;
      }

      if (response.ok) {
        try {
          const responseText = await response.text();

          let responseData;
          try {
            responseData = JSON.parse(responseText);
          } catch {
            responseData = { text: responseText };
          }

          if (
            (item.action === 'create' || item.action === 'update') &&
            responseData.id &&
            responseData.refId
          ) {
            const updatedData = {
              ...item.data,
              ...responseData.data,
              id: responseData.id,
              refId: responseData.refId,
            };

            await indexedDBManager.updateLocalData(
              item.entity,
              String(item.data.refId),
              updatedData
            );

            await this.markLocalDataAsSynced(item.entity, String(item.data.refId));
          } else if (responseData.status === 'queued') {
            await this.markLocalDataAsSynced(item.entity, String(item.data.refId || item.data.id));
          }
        } catch (error) {
          // Error updating local data after sync
        }
      }

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  private getEndpointForEntity(entity: string): string {
    switch (entity) {
      case 'presentation':
        return `/${API_ENDPOINTS.PRESENTATION.PRESENTATIONS}`;
      case 'price':
        return `/${API_ENDPOINTS.PRICE.PRICES}`;
      case 'inspection':
        return `/${API_ENDPOINTS.INSPECTION.INSPECTIONS}`;
      case 'task':
        return `/${API_ENDPOINTS.TASK.CREATE_TASK}`;
      default:
        throw new Error(`Unknown entity: ${entity}`);
    }
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      return token || null;
    } catch {
      return null;
    }
  }

  async addToSyncQueue(
    entity: string,
    action: 'create' | 'update' | 'soft-delete',
    data: Record<string, unknown>
  ): Promise<void> {
    await indexedDBManager.addToSyncQueue({
      entity,
      action,
      data,
      timestamp: Date.now(),
      retryCount: 0,
      synced: 0,
    });
  }

  async forceSync(): Promise<SyncResult> {
    return await this.sync();
  }

  async getSyncStatus(): Promise<{
    pendingItems: number;
    lastSyncTime: number | null;
    isOnline: boolean;
  }> {
    const unsyncedItems = await indexedDBManager.getUnsyncedItems();
    const lastSyncTime = await indexedDBManager.getSetting('lastSyncTime');

    return {
      pendingItems: unsyncedItems.length,
      lastSyncTime:
        typeof lastSyncTime === 'object' && lastSyncTime !== null
          ? (lastSyncTime as any)?.lastSyncTime || null
          : typeof lastSyncTime === 'number'
            ? lastSyncTime
            : null,
      isOnline: navigator.onLine,
    };
  }

  updateConfig(newConfig: Partial<SyncConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  private async markLocalDataAsSynced(entity: string, id: string): Promise<void> {
    try {
      const storeName = indexedDBManager['getStoreName'](entity);
      const transaction = indexedDBManager['db']?.transaction([storeName], 'readwrite');
      if (!transaction) {
        return;
      }

      const store = transaction.objectStore(storeName);
      const existing = await indexedDBManager['promisifyRequest'](store.get(id));

      if (existing) {
        existing.synced = 1;
        existing.timestamp = Date.now();
        await indexedDBManager['promisifyRequest'](store.put(existing));
        window.dispatchEvent(new CustomEvent('sync-completed', { detail: { entity, id } }));
      }
    } catch (error) {
      // Error marking item as synced
    }
  }
}

export const syncEngine = new SyncEngine();
